<template>
  <v-row justify="center">
    <v-dialog v-model="dialog" persistent max-width="55rem" width="95%">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center px-4">
          <span class="text-h6">Datos personales</span>
          <v-btn
            @click="closeModal"
            :icon="enums.icons.CLOSE_ICON"
            variant="text"
            size="small"
            id="v-btn-close-datos-personales"
            class="ml-auto"
            style="margin-right: -8px"
          >
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4 pt-0">
          <!-- Skeleton Loader -->
          <template v-if="loading">
            <v-skeleton-loader
              type="card, card"
              class="skeleton-container"
            ></v-skeleton-loader>
          </template>

          <!-- Contenido real -->
          <v-form
            v-model="isFormValid"
            ref="form"
            @submit.prevent="actualizarDatos()"
            v-else
          >
            <v-container class="pa-0">
              <!-- Tarjeta Datos generales -->
              <v-row>
                <v-col cols="12">
                  <v-card variant="outlined">
                    <v-card-title
                      class="text-subtitle-1 font-weight-bold pb-4 text-primary"
                    >
                      Datos generales
                    </v-card-title>

                    <v-card-text>
                      <v-row class="py-0">
                        <!-- Nombre -->
                        <v-col cols="12" md="6" class="py-0">
                          <v-text-field
                            :label="
                              isPrestador ? 'Nombre/s y apellido/s' : 'Nombre'
                            "
                            v-model="userData.nombre"
                            readonly
                            variant="outlined"
                            density="compact"
                          ></v-text-field>
                        </v-col>

                        <!-- Email -->
                        <v-col cols="12" md="6" class="py-0">
                          <v-text-field
                            label="Email (*)"
                            v-model="userData.email"
                            variant="outlined"
                            density="compact"
                            :rules="[rules.required, rules.emailRules].flat()"
                            maxlength="100"
                            required
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Tarjeta Datos por financiador (solo para Emisor, Prestador y Farmacia) -->
              <v-row v-if="shouldShowDatosPorFinanciador">
                <v-col cols="12">
                  <v-card variant="outlined">
                    <v-card-title
                      class="text-subtitle-1 font-weight-bold pb-4 text-primary"
                    >
                      {{ tituloFinanciador }}
                    </v-card-title>

                    <v-card-text>
                      <!-- Combo de Financiadores (solo si hay más de 1) -->
                      <template v-if="shouldShowComboFinanciadores">
                        <v-row class="py-0">
                          <v-col cols="12" md="6" class="py-0">
                            <v-select
                              label="Seleccionar financiador"
                              v-model="financiadorSeleccionado"
                              :items="financiadoresFiltrados"
                              item-title="value"
                              item-value="id"
                              variant="outlined"
                              density="compact"
                              @update:model-value="onFinanciadorChange"
                            ></v-select>
                          </v-col>
                          <!-- Combo de Matrículas (solo para prestadores con múltiples matrículas) -->
                          <v-col 
                            cols="12" 
                            md="6" 
                            class="py-0"
                            v-if="shouldShowComboMatriculas"
                          >
                            <v-select
                              label="Seleccionar matrícula"
                              v-model="matriculaSeleccionada"
                              :items="matriculasDisponibles"
                              item-title="display"
                              item-value="preXfinId"
                              variant="outlined"
                              density="compact"
                              @update:model-value="onMatriculaChange"
                            ></v-select>
                          </v-col>
                          <!-- Texto de matrícula única -->
                          <v-col 
                            cols="12" 
                            md="6" 
                            class="py-0"
                            v-else-if="shouldShowTextoMatriculaUnica"
                          >
                            <v-text-field
                              label="Matrícula"
                              variant="outlined"
                              density="compact"
                              readonly
                              :model-value="matriculaUnicaDisplay"
                              tabindex="-1"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </template>

                      <!-- Fila para financiador único con matrícula -->
                      <template v-else-if="!shouldShowComboFinanciadores && isUserPrestador">
                        <v-row class="py-0">
                          <!-- Combo de Matrículas (solo para prestadores con múltiples matrículas) -->
                          <v-col 
                            cols="12" 
                            md="6" 
                            class="py-0"
                            v-if="shouldShowComboMatriculas"
                          >
                            <v-select
                              label="Seleccionar matrícula"
                              v-model="matriculaSeleccionada"
                              :items="matriculasDisponibles"
                              item-title="display"
                              item-value="preXfinId"
                              variant="outlined"
                              density="compact"
                              @update:model-value="onMatriculaChange"
                            ></v-select>
                          </v-col>
                          <!-- Texto de matrícula única -->
                          <v-col 
                            cols="12" 
                            md="6" 
                            class="py-0"
                            v-else-if="shouldShowTextoMatriculaUnica"
                          >
                            <v-text-field
                              label="Matrícula"
                              variant="outlined"
                              density="compact"
                              readonly
                              :model-value="matriculaUnicaDisplay"
                              tabindex="-1"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </template>

                      <!-- Información del financiador seleccionado -->
                      <template v-if="financiadorSeleccionado">
                        <!-- Loading de datos por financiador -->
                        <template v-if="loadingDatosFinanciador">
                          <v-skeleton-loader
                            type="text@3"
                            class="mt-2"
                          ></v-skeleton-loader>
                        </template>

                        <!-- Datos cargados -->
                        <template v-else-if="datosPorFinanciador">
                          <v-row class="py-0">
                            <!-- Campos editables según tipo de entidad -->

                            <!-- Campos para prestador -->
                            <template
                              v-if="
                                datosPorFinanciador.tipoEntidad === 'Prestador'
                              "
                            >
                              <!-- Teléfono -->
                              <v-col cols="12" md="6" class="py-0">
                                <v-text-field
                                  label="Teléfono"
                                  v-model="datosPorFinanciador.telefono"
                                  variant="outlined"
                                  density="compact"
                                  :rules="
                                    datosPorFinanciador.telefono
                                      ? [rules.telephoneRules]
                                          .concat(
                                            rules.maxLength(
                                              datosPorFinanciador.telefono,
                                              15
                                            )
                                          )
                                          .flat()
                                      : []
                                  "
                                  maxlength="15"
                                  v-mask="'###############'"
                                ></v-text-field>
                              </v-col>

                              <!-- Dirección -->
                              <v-col cols="12" md="6" class="py-0">
                                <v-text-field
                                  label="Dirección"
                                  v-model="datosPorFinanciador.direccion"
                                  variant="outlined"
                                  density="compact"
                                  :rules="
                                    rules.required.concat([
                                      rules.maxLength(
                                        datosPorFinanciador.direccion,
                                        150
                                      ),
                                    ])
                                  "
                                  maxlength="150"
                                ></v-text-field>
                              </v-col>

                              <!-- Mostrar datos personales en reporte -->
                              <v-col cols="12" md="6" class="py-0 pl-5">
                                <v-switch
                                  v-model="datosPorFinanciador.mostrarDatosPersonalesReporte"
                                  label="Imprimir datos personales en reporte"
                                  color="primary"
                                  density="compact"
                                  hide-details
                                ></v-switch>
                              </v-col>

                              <!-- Switch para establecer como matrícula por defecto -->
                              <v-col cols="12" md="6" class="py-0 pl-5">
                                <v-switch
                                  v-model="establecerComoDefault"
                                  label="Establecer como matrícula por defecto"
                                  color="primary"
                                  density="compact"
                                  hide-details
                                  :disabled="false"
                                ></v-switch>
                              </v-col>
                            </template>

                            <!-- Campos para farmacia -->
                            <template
                              v-if="
                                datosPorFinanciador.tipoEntidad === 'Farmacia'
                              "
                            >
                              <!-- Teléfono -->
                              <v-col cols="12" md="4" class="py-0">
                                <v-text-field
                                  label="Teléfono"
                                  v-model="datosPorFinanciador.telefono"
                                  variant="outlined"
                                  density="compact"
                                  :rules="
                                    datosPorFinanciador.telefono
                                      ? [rules.telephoneRules]
                                          .concat(
                                            rules.maxLength(
                                              datosPorFinanciador.telefono,
                                              15
                                            )
                                          )
                                          .flat()
                                      : []
                                  "
                                  maxlength="15"
                                  v-mask="'###############'"
                                ></v-text-field>
                              </v-col>

                              <!-- Dirección -->
                              <v-col cols="12" md="8" class="py-0">
                                <v-text-field
                                  label="Dirección"
                                  v-model="datosPorFinanciador.direccion"
                                  variant="outlined"
                                  density="compact"
                                  :rules="
                                    datosPorFinanciador.direccion
                                      ? [
                                          rules.maxLength(
                                            datosPorFinanciador.direccion,
                                            100
                                          ),
                                        ].flat()
                                      : []
                                  "
                                  maxlength="100"
                                ></v-text-field>
                              </v-col>
                            </template>

                            <!-- Campos para emisor -->
                            <template
                              v-if="
                                datosPorFinanciador.tipoEntidad === 'Emisor'
                              "
                            >
                              <!-- Teléfono -->
                              <v-col cols="12" md="4" class="py-0">
                                <v-text-field
                                  label="Teléfono"
                                  v-model="datosPorFinanciador.telefono"
                                  variant="outlined"
                                  density="compact"
                                  :rules="
                                    datosPorFinanciador.telefono
                                      ? [rules.telephoneRules]
                                          .concat(
                                            rules.maxLength(
                                              datosPorFinanciador.telefono,
                                              15
                                            )
                                          )
                                          .flat()
                                      : []
                                  "
                                  maxlength="15"
                                  v-mask="'###############'"
                                ></v-text-field>
                              </v-col>
                            </template>
                          </v-row>
                        </template>

                        <!-- Sin datos -->
                        <template v-else>
                          <v-card
                            variant="outlined"
                            color="grey-lighten-4"
                            class="pa-3 mt-2"
                          >
                            <p
                              class="text-body-2 text-medium-emphasis text-center mb-0"
                            >
                              No hay datos disponibles para este financiador
                            </p>
                          </v-card>
                        </template>
                      </template>

                      <!-- Mensaje cuando no hay financiador -->
                      <template
                        v-else-if="
                          !shouldShowComboFinanciadores &&
                          financiadoresRelacionados.length === 0
                        "
                      >
                        <v-card
                          variant="outlined"
                          color="grey-lighten-4"
                          class="pa-3"
                        >
                          <p
                            class="text-body-2 text-medium-emphasis text-center mb-0"
                          >
                            No hay financiadores relacionados
                          </p>
                        </v-card>
                      </template>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Botón Actualizar único al final -->
              <v-card-actions class="justify-end pa-0">
                <v-btn
                  color="primary"
                  variant="outlined"
                  size="small"
                  type="submit"
                  id="v-btn-actualizar-datos"
                  class="text-capitalize"
                  :disabled="!isFormValid || isUpdating"
                  :loading="isUpdating"
                >
                  <template v-if="isUpdating"> Actualizando... </template>
                  <template v-else> Actualizar </template>
                </v-btn>
              </v-card-actions>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import enums from "@/utils/enums/index";
import rules from "@/utils/helpers/rules";
import { mapActions } from "vuex";

export default {
  name: "DatosPersonales",
  props: {
    userFilters: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialog: true,
      enums: enums,
      rules: rules,
      // Datos del usuario
      userData: {
        email: "",
        nombre: "",
      },
      // Variables para tipo de usuario (solo los que usan datos personales)
      isUserEmisor: false,
      isUserFarmacia: false,
      isUserPrestador: false,
      // Estado de carga
      loading: true,
      // Financiadores relacionados
      financiadoresRelacionados: [],
      financiadorSeleccionado: null,
      financiadoresList: [], // Lista completa de financiadores
      // Datos por financiador
      datosPorFinanciador: null,
      loadingDatosFinanciador: false,
      // Estado del formulario
      isFormValid: false,
      // Estado de carga para actualización
      isUpdating: false,
      // Variables para matrículas
      matriculasDisponibles: [],
      matriculaSeleccionada: null,
      establecerComoDefault: false,
    };
  },
  computed: {
    isPrestador() {
      return this.isUserPrestador;
    },
    shouldShowDatosPorFinanciador() {
      // Solo para Emisor, Prestador y Farmacia
      return this.isUserEmisor || this.isUserPrestador || this.isUserFarmacia;
    },
    shouldShowComboFinanciadores() {
      // Solo mostrar combo si hay más de 1 financiador relacionado
      return this.financiadoresRelacionados.length > 1;
    },
    financiadoresFiltrados() {
      // Filtrar financiadores según los relacionados al usuario
      if (!this.financiadoresList || !Array.isArray(this.financiadoresList)) {
        return [];
      }
      return this.financiadoresList.filter(
        (financiador) =>
          financiador && this.financiadoresRelacionados.includes(financiador.id)
      );
    },
    tituloFinanciador() {
      let titulo = "Datos por financiador";

      // Si hay solo un financiador y está seleccionado automáticamente
      if (
        this.financiadoresRelacionados.length === 1 &&
        this.financiadorSeleccionado
      ) {
        const financiadorEncontrado = this.financiadoresList.find(
          (f) => f && f.id === this.financiadorSeleccionado
        );
        if (financiadorEncontrado) {
          titulo += ` - ${financiadorEncontrado.value}`;
        }
      }

      return titulo;
    },
    shouldShowComboMatriculas() {
      // Solo mostrar combo si es prestador y hay más de 1 matrícula
      return this.isUserPrestador && this.matriculasDisponibles.length > 1;
    },
    shouldShowTextoMatriculaUnica() {
      // Solo mostrar texto si es prestador y hay exactamente 1 matrícula
      return this.isUserPrestador && this.matriculasDisponibles.length === 1;
    },
    matriculaUnicaDisplay() {
      // Mostrar la descripción de la única matrícula disponible
      return this.matriculasDisponibles.length === 1 ? this.matriculasDisponibles[0].display : "";
    },
  },
  created() {
    this.loadUserData();
  },
  methods: {
    ...mapActions({
      getFinanciadores: "taxonomy/getFinanciadores",
      getDatosGeneralesUsuario: "user/getDatosGeneralesUsuario",
      getDatosPorFinanciador: "user/getDatosPorFinanciador",
      getDatosPorMatricula: "user/getDatosPorMatricula",
      actualizarDatosPersonales: "user/actualizarDatosPersonales",
      setAlert: "user/setAlert",
    }),
    closeModal() {
      this.$emit("close");
    },
    async actualizarDatos() {
      if (this.isUpdating) return; // Prevenir múltiples llamadas

      this.isUpdating = true;
      try {
        // Preparar el payload para el API
        const payload = {
          email: this.userData.email,
          telefono: this.datosPorFinanciador?.telefono || null,
          observaciones: this.datosPorFinanciador?.observaciones || null,
          direccion: this.datosPorFinanciador?.direccion || null,
          financiadorId: this.financiadorSeleccionado || null,
          preXfinId: this.matriculaSeleccionada || this.datosPorFinanciador?.preXfinId || null,
          mostrarDatosPersonalesReporte: this.datosPorFinanciador?.mostrarDatosPersonalesReporte || false,
          esMatriculaPorDefecto: this.isUserPrestador ? this.establecerComoDefault : null,
        };

        // Llamar al API
        const response = await this.actualizarDatosPersonales(payload);

        if (response.status === 200) {
          this.setAlert({
            type: "success",
            message: "Datos actualizados correctamente",
          });

          // Opcional: recargar los datos para reflejar los cambios
          await this.loadDatosGenerales();
          if (this.financiadorSeleccionado) {
            await this.loadDatosPorFinanciador();
          }
        }
      } catch (error) {
        console.error("Error al actualizar datos:", error);
      } finally {
        this.isUpdating = false;
      }
    },
    async loadUserData() {
      try {
        // Cargar financiadores completos
        try {
          this.financiadoresList = (await this.getFinanciadores()) || [];
        } catch (financiadoresError) {
          console.error("Error al cargar financiadores:", financiadoresError);
          this.financiadoresList = [];
        }

        // Usar los datos pasados por props desde NavBar
        const filters = this.userFilters;

        // Determinar tipo de usuario y financiadores relacionados
        if (filters && filters.emisorId !== null) {
          this.isUserEmisor = true;
          // Financiadores relacionados al emisor
          if (
            filters.financiadoresxEmisor &&
            filters.financiadoresxEmisor.length > 0
          ) {
            this.financiadoresRelacionados = filters.financiadoresxEmisor;
            this.setFinanciadorSeleccionado();
          }
        } else if (filters && filters.farmaciaId !== null) {
          this.isUserFarmacia = true;
          if (
            filters.financiadoresXFarmacia &&
            filters.financiadoresXFarmacia.length > 0
          ) {
            this.financiadoresRelacionados = filters.financiadoresXFarmacia;
            this.setFinanciadorSeleccionado();
          }
        } else if (filters && filters.preId !== null) {
          this.isUserPrestador = true;
          // Financiadores relacionados al prestador
          if (
            filters.financiadoresXPrestador &&
            filters.financiadoresXPrestador.length > 0
          ) {
            this.financiadoresRelacionados = filters.financiadoresXPrestador;
            this.setFinanciadorSeleccionado();
          }
        }

        // Cargar datos generales del usuario usando la API
        await this.loadDatosGenerales();
      } catch (error) {
        console.error("Error al cargar los datos del usuario", error);
      } finally {
        // Siempre ocultar el loading, independientemente del resultado
        this.loading = false;
      }
    },
    async setFinanciadorSeleccionado() {
      // Si solo hay 1 financiador, seleccionarlo automáticamente
      if (this.financiadoresRelacionados.length === 1) {
        this.financiadorSeleccionado = this.financiadoresRelacionados[0];
        // Cargar datos automáticamente para el financiador único
        await this.loadDatosPorFinanciador();
      }
      // Si hay más de 1, dejar que el usuario seleccione
    },
    async onFinanciadorChange() {
      if (this.financiadorSeleccionado) {
        await this.loadDatosPorFinanciador();
      }
    },
    async onMatriculaChange() {
      if (this.matriculaSeleccionada && this.financiadorSeleccionado) {
        this.loadingDatosFinanciador = true;
        try {
          const datosMatricula = await this.getDatosPorMatricula({
            financiadorId: this.financiadorSeleccionado,
            preXfinId: this.matriculaSeleccionada
          });
          
          if (datosMatricula) {
            // Actualizar solo los datos específicos, manteniendo el resto
            this.datosPorFinanciador = {
              ...this.datosPorFinanciador,
              telefono: datosMatricula.telefono,
              direccion: datosMatricula.direccion,
              mostrarDatosPersonalesReporte: datosMatricula.mostrarDatosPersonalesReporte,
              preXfinId: datosMatricula.preXfinId
            };
            
            // Actualizar el switch de matrícula por defecto según la matrícula seleccionada
            const matriculaSeleccionadaData = this.matriculasDisponibles.find(m => m.preXfinId === this.matriculaSeleccionada);
            this.establecerComoDefault = matriculaSeleccionadaData?.esMatriculaPorDefecto || false;
          }
        } catch (error) {
          console.error("Error al cargar datos de la matrícula:", error);
          this.setAlert({
            type: "error",
            message: "Error al cargar los datos de la matrícula seleccionada.",
          });
        } finally {
          this.loadingDatosFinanciador = false;
        }
      }
    },
    async loadDatosGenerales() {
      try {
        const datosGenerales = await this.getDatosGeneralesUsuario();
        if (datosGenerales) {
          // Actualizar userData con los datos reales de la API
          this.userData = {
            ...this.userData,
            email: datosGenerales.email || this.userData.email,
            nombre: datosGenerales.nombre || this.userData.nombre,
          };
        }
      } catch (error) {
        console.error("Error al cargar datos generales:", error);
      }
    },
    async loadDatosPorFinanciador() {
      if (!this.financiadorSeleccionado) return;

      this.loadingDatosFinanciador = true;
      try {
        this.datosPorFinanciador = await this.getDatosPorFinanciador(
          this.financiadorSeleccionado
        );

        // Si es prestador, configurar las matrículas disponibles
        if (this.datosPorFinanciador && this.datosPorFinanciador.tipoEntidad === "Prestador") {
          this.matriculasDisponibles = this.datosPorFinanciador.matriculasDisponibles || [];
          
          // Seleccionar automáticamente la primera matrícula o la marcada como default
          if (this.matriculasDisponibles.length > 0) {
            const matriculaDefault = this.matriculasDisponibles.find(m => m.esDefault) || this.matriculasDisponibles[0];
            this.matriculaSeleccionada = matriculaDefault.preXfinId;
            
            // Establecer el valor inicial del switch según la matrícula seleccionada
            this.establecerComoDefault = matriculaDefault.esMatriculaPorDefecto || false;
          } else {
            this.matriculaSeleccionada = null;
            this.establecerComoDefault = false;
          }
        } else {
          // Limpiar datos de matrículas para otros tipos de entidades
          this.matriculasDisponibles = [];
          this.matriculaSeleccionada = null;
        }
      } catch (error) {
        console.error("Error al cargar datos por financiador:", error);
        this.datosPorFinanciador = null;
        this.matriculasDisponibles = [];
        this.matriculaSeleccionada = null;
      } finally {
        this.loadingDatosFinanciador = false;
      }
    },
  },
};
</script>

<style scoped>
/* Estilos específicos para el modal de datos personales */
.text-primary {
  color: #242059 !important;
}

.v-text-field {
  margin-bottom: 0 !important;
}

/* Estilos para el skeleton loader */
.skeleton-container {
  min-height: 400px;
}

.skeleton-container .v-skeleton-loader__bone {
  margin-bottom: 16px;
}

/* Estilos para las tarjetas */
.v-card .v-card-text {
  padding-bottom: 16px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .v-dialog {
    margin: 16px;
  }

  .v-card-title {
    font-size: 1.125rem !important;
    padding: 16px !important;
  }

  .v-card-text {
    padding: 16px !important;
    padding-top: 0 !important;
  }
}

@media (max-width: 400px) {
  .text-subtitle-1 {
    font-size: 1rem !important;
  }
}
</style>
